import { useState } from "react";

import {
  Button,
  type Column,
  EmptyState,
  Modal,
  Pill,
  SearchMultiSelect,
  Table,
  Toggle,
} from "@/components/common";
import { Guard } from "@/auth/Guard";
import {
  useContractTemplates,
  useCreateContractTemplate,
  useDeleteContractTemplate,
  useUpdateContractTemplate,
} from "@/hooks/useContractTemplates";
import { useTeses } from "@/hooks/useTeses";
import { extractErrorMessage } from "@/services/axiosConfig";
import type {
  ContractTemplate,
  ContractTemplateCreatePayload,
} from "@/types/contractTemplate";
import { formatPct } from "@/utils/fmt";
import { fractionToPct, pctToFractionOrNull } from "@/utils/pct";

type Props = { workspaceId: number };

export function ContractTemplatesPanel({ workspaceId }: Props) {
  const { data: templates = [], isLoading } = useContractTemplates(workspaceId);
  const deleteTemplate = useDeleteContractTemplate(workspaceId);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ContractTemplate | null>(null);

  const columns: Column<ContractTemplate>[] = [
    {
      id: "name",
      header: "Template",
      cell: (t) => <span className="razao">{t.name}</span>,
    },
    {
      id: "fee",
      header: "Success fee",
      align: "right",
      cell: (t) => formatPct(t.success_fee_pct),
    },
    {
      id: "share",
      header: "Participação TD",
      align: "right",
      cell: (t) => formatPct(t.td_share_pct),
    },
    {
      id: "vigencia",
      header: "Vigência",
      align: "right",
      cell: (t) => (t.vigencia_meses != null ? `${t.vigencia_meses} meses` : "—"),
    },
    {
      id: "flags",
      header: "",
      cell: (t) => (
        <div className="ctr-row-actions" style={{ justifyContent: "flex-start" }}>
          {t.is_default && <Pill tone="diag">Padrão</Pill>}
          <Pill tone={t.active ? "ativa" : "pausada"}>{t.active ? "Ativo" : "Inativo"}</Pill>
        </div>
      ),
    },
  ];

  return (
    <div className="ctr-detail">
      <Guard permission="contracts.write">
        <div className="emp-toolbar">
          <div className="emp-toolbar-spacer" style={{ flex: 1 }} />
          <Button variant="primary" iconLeft="plus" onClick={() => setCreating(true)}>
            Novo template
          </Button>
        </div>
      </Guard>

      <Table
        rows={templates}
        columns={columns}
        getKey={(t) => String(t.id)}
        onRowClick={(t) => setEditing(t)}
        rowAction={(t) => (
          <Guard permission="contracts.write">
            <div className="ctr-row-actions">
              <Button variant="ghost" size="sm" iconLeft="settings" onClick={() => setEditing(t)} />
              <Button
                variant="ghost"
                size="sm"
                iconLeft="x"
                disabled={deleteTemplate.isPending}
                onClick={() => {
                  if (confirm(`Excluir o template "${t.name}"?`)) {
                    void deleteTemplate.mutateAsync(t.id);
                  }
                }}
              />
            </div>
          </Guard>
        )}
        empty={
          <EmptyState
            icon="file-text"
            size="lg"
            title={isLoading ? "Carregando…" : "Nenhum template"}
            description={
              isLoading ? undefined : "Crie um template para padronizar novos contratos."
            }
          />
        }
      />

      {creating && (
        <TemplateModal workspaceId={workspaceId} onClose={() => setCreating(false)} />
      )}
      {editing && (
        <TemplateModal
          workspaceId={workspaceId}
          template={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

// ─── Create / edit modal ─────────────────────────────────────────────────────

function TemplateModal({
  workspaceId,
  template,
  onClose,
}: {
  workspaceId: number;
  template?: ContractTemplate;
  onClose: () => void;
}) {
  const isEdit = template != null;
  const create = useCreateContractTemplate(workspaceId);
  const update = useUpdateContractTemplate(workspaceId);
  const { data: teses = [] } = useTeses(true);

  const [name, setName] = useState(template?.name ?? "");
  const [description, setDescription] = useState(template?.description ?? "");
  const [body, setBody] = useState(template?.body ?? "");
  const [vigenciaMeses, setVigenciaMeses] = useState(
    template?.vigencia_meses != null ? String(template.vigencia_meses) : "",
  );
  const [successFeePct, setSuccessFeePct] = useState(
    fractionToPct(template?.success_fee_pct) ?? "",
  );
  const [tdSharePct, setTdSharePct] = useState(fractionToPct(template?.td_share_pct) ?? "");
  const [teseIds, setTeseIds] = useState<number[]>(template?.default_tese_ids ?? []);
  const [isDefault, setIsDefault] = useState(template?.is_default ?? false);
  const [active, setActive] = useState(template?.active ?? true);
  const [error, setError] = useState<string | null>(null);

  const pending = create.isPending || update.isPending;
  const valid = name.trim() !== "";

  async function submit() {
    if (!valid) return;
    setError(null);
    const payload: ContractTemplateCreatePayload = {
      name: name.trim(),
      description: description.trim() || null,
      body: body.trim() || null,
      vigencia_meses: vigenciaMeses.trim() ? Number(vigenciaMeses) : null,
      success_fee_pct: pctToFractionOrNull(successFeePct),
      td_share_pct: pctToFractionOrNull(tdSharePct),
      default_tese_ids: teseIds,
      is_default: isDefault,
      active,
    };
    try {
      if (isEdit) {
        await update.mutateAsync({ id: template.id, payload });
      } else {
        await create.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={isEdit ? `Editar template · ${template.name}` : "Novo template"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!valid || pending} onClick={() => void submit()}>
            {pending ? "Salvando…" : isEdit ? "Salvar" : "Criar template"}
          </Button>
        </>
      }
    >
      <div className="ctr-section">
        {error && <p className="td-danger">{error}</p>}

        <label className="ctr-field">
          <span>Nome</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Contrato padrão Tributo Devido"
          />
        </label>

        <label className="ctr-field">
          <span>Descrição</span>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="opcional"
          />
        </label>

        <label className="ctr-field">
          <span>Cláusulas</span>
          <textarea
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Corpo / cláusulas do contrato (opcional)"
          />
        </label>

        <label className="ctr-field">
          <span>Vigência (meses)</span>
          <input
            type="number"
            min={0}
            step="1"
            value={vigenciaMeses}
            onChange={(e) => setVigenciaMeses(e.target.value)}
            placeholder="opcional"
          />
        </label>

        <div className="ctr-field-row">
          <label className="ctr-field">
            <span>Success fee (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={successFeePct}
              onChange={(e) => setSuccessFeePct(e.target.value)}
              placeholder="opcional"
            />
          </label>
          <label className="ctr-field">
            <span>Participação TD (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={tdSharePct}
              onChange={(e) => setTdSharePct(e.target.value)}
              placeholder="opcional"
            />
          </label>
        </div>

        <label className="ctr-field">
          <span>Teses padrão</span>
          <SearchMultiSelect
            values={teseIds.map(String)}
            onChange={(ids) => setTeseIds(ids.map(Number))}
            options={teses.map((t) => ({
              id: String(t.id),
              label: t.nome,
              sub: t.descricao ?? t.slug,
            }))}
            placeholder="Buscar e selecionar teses…"
            emptyLabel="Nenhuma tese encontrada."
          />
        </label>

        <Toggle
          checked={isDefault}
          onChange={setIsDefault}
          label="Template padrão"
          sub="Pré-selecionado ao criar um novo contrato."
        />
        <Toggle
          checked={active}
          onChange={setActive}
          label="Ativo"
          sub="Templates inativos não aparecem no wizard."
        />
      </div>
    </Modal>
  );
}
